<template>
  <div class="machine-advert-group">
    <!-- Search Bar -->
    <div class="el-search vue-top-padding radius">
      <el-form inline>
        <el-form-item>
          <el-input
            v-model="filters.equipmentNo"
            placeholder="Machine No"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model="filters.equipmentName"
            placeholder="Machine Name"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model="filters.groupName"
            placeholder="Machine Group"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">Search</el-button>
          <el-button @click="handleReset">Reset</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- Table -->
    <div class="vue-padding radius">
      <el-table
        v-loading="loading"
        :data="machines"
        :element-loading-text="$t('common.loading')"
        :empty-text="$t('common.noData')"
        max-height="638"
        :border="true"
        style="width: 100%"
      >
        <el-table-column prop="name" label="Machine Name" :show-overflow-tooltip="true" />
        <el-table-column prop="machineId" label="Machine No" width="180" />
        <el-table-column prop="groupDisplay" label="Machine Group" />
        <el-table-column label="Ad Count" width="160" align="center">
          <template slot-scope="scope">
            <span>
              <i class="el-icon-picture"></i> {{ scope.row.userAdCount != null ? scope.row.userAdCount : scope.row.adCount }}
              &nbsp;
              <span class="paid-ads-count">
                <i class="el-icon-connection"></i> {{ scope.row.paidAdCount != null ? scope.row.paidAdCount : scope.row.paidAds }}
              </span>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="160" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" icon="el-icon-view" @click="viewAds(scope.row)">View Ads</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="currentPage"
        :page-size="pageSize"
        :total="totalCount"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- Ads Dialog -->
    <el-dialog append-to-body
      :visible.sync="showAdsModal"
      :title="selectedMachine ? `Ads on ${selectedMachine.name}${selectedMachine.machineId ? ' (' + selectedMachine.machineId + ')' : ''}` : 'View Ads'"
      width="700px"
      @close="closeModal"
    >
      <div v-if="selectedMachine">
        <!-- User Ads Section -->
        <div class="ads-section">
          <h3 class="section-title">
            User Ads
            <el-tag size="mini" type="info" style="margin-left: 8px;">{{ userAdsList.length }}</el-tag>
          </h3>
          <template v-if="userAdsList.length > 0">
            <!-- Individual Ads -->
            <div v-if="individualUserAds.length > 0" class="user-ads-subsection">
              <h4 class="subsection-title">Individual Ads (Gallery assign)</h4>
              <div class="ads-list">
                <div
                  v-for="(ad, idx) in individualUserAds"
                  :key="'user-ind-' + (ad.id || idx) + '-' + idx"
                  class="ad-item"
                >
                  <div class="ad-top-row">
                    <video
                      v-if="ad.isVideo"
                      :src="ad.image"
                      class="ad-image ad-video"
                      muted loop playsinline preload="metadata"
                    ></video>
                    <img v-else :src="ad.image" :alt="ad.title" class="ad-image" @error="onAdImageError" />
                    <div class="ad-info">
                      <h4 class="ad-title">{{ ad.title }}</h4>
                      <p class="ad-brand">{{ ad.brand }}</p>
                    </div>
                    <el-tag size="mini" :type="ad.displayLabel === 'Banner' ? 'warning' : 'info'">
                      <i :class="ad.displayLabel === 'Banner' ? 'el-icon-picture' : 'el-icon-monitor'"></i>
                      {{ ad.displayLabel || 'Screen' }}
                    </el-tag>
                  </div>
                  <div class="ad-bottom-row">
                    <el-date-picker
                      v-model="ad.dateRange"
                      type="datetimerange"
                      range-separator="-"
                      start-placeholder="Start Date & Time"
                      end-placeholder="End Date & Time"
                      value-format="yyyy-MM-dd HH:mm:ss"
                      format="yyyy-MM-dd HH:mm:ss"
                      :default-time="['00:00:00', '23:59:59']"
                      :disabled="!ad.isDateEditable"
                      size="small"
                    />
                    <div class="ad-actions">
                      <el-button
                        size="mini"
                        :icon="ad.isDateEditable ? 'el-icon-check' : 'el-icon-edit'"
                        :disabled="ad.savingDates"
                        @click="toggleUserAdEdit(ad)"
                      />
                      <el-button
                        size="mini"
                        type="danger"
                        icon="el-icon-delete"
                        @click="removeUserAd(ad)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Group Ads -->
            <div v-if="groupUserAds.length > 0" class="user-ads-subsection">
              <h4 class="subsection-title">Group Ads</h4>
              <div class="ads-list">
                <div
                  v-for="(ad, idx) in groupUserAds"
                  :key="'user-grp-' + (ad.id || idx) + '-' + idx"
                  class="ad-item"
                >
                  <div class="ad-top-row">
                    <video
                      v-if="ad.isVideo"
                      :src="ad.image"
                      class="ad-image ad-video"
                      muted loop playsinline preload="metadata"
                    ></video>
                    <img v-else :src="ad.image" :alt="ad.title" class="ad-image" @error="onAdImageError" />
                    <div class="ad-info">
                      <h4 class="ad-title">{{ ad.title }}</h4>
                      <p class="ad-brand">{{ ad.brand }}</p>
                      <p v-if="ad.adGroupName" class="ad-group-name">{{ ad.adGroupName }}</p>
                    </div>
                    <el-tag size="mini" :type="ad.displayLabel === 'Banner' ? 'warning' : 'info'">
                      <i :class="ad.displayLabel === 'Banner' ? 'el-icon-picture' : 'el-icon-monitor'"></i>
                      {{ ad.displayLabel || 'Screen' }}
                    </el-tag>
                  </div>
                  <div class="ad-bottom-row ad-bottom-row-actions-only">
                    <div class="ad-actions ad-actions-end">
                      <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeUserAd(ad)" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Other assignments -->
            <div v-if="otherUserAds.length > 0" class="user-ads-subsection">
              <h4 class="subsection-title">Other assignments</h4>
              <div class="ads-list">
                <div
                  v-for="(ad, idx) in otherUserAds"
                  :key="'user-oth-' + (ad.id || idx) + '-' + idx"
                  class="ad-item"
                >
                  <div class="ad-top-row">
                    <video
                      v-if="ad.isVideo"
                      :src="ad.image"
                      class="ad-image ad-video"
                      muted loop playsinline preload="metadata"
                    ></video>
                    <img v-else :src="ad.image" :alt="ad.title" class="ad-image" @error="onAdImageError" />
                    <div class="ad-info">
                      <h4 class="ad-title">{{ ad.title }}</h4>
                      <p class="ad-brand">{{ ad.brand }}</p>
                    </div>
                    <el-tag size="mini" :type="ad.displayLabel === 'Banner' ? 'warning' : 'info'">
                      <i :class="ad.displayLabel === 'Banner' ? 'el-icon-picture' : 'el-icon-monitor'"></i>
                      {{ ad.displayLabel || 'Screen' }}
                    </el-tag>
                  </div>
                  <div class="ad-bottom-row">
                    <el-date-picker
                      v-model="ad.dateRange"
                      type="datetimerange"
                      range-separator="-"
                      start-placeholder="Start Date & Time"
                      end-placeholder="End Date & Time"
                      value-format="yyyy-MM-dd HH:mm:ss"
                      format="yyyy-MM-dd HH:mm:ss"
                      :default-time="['00:00:00', '23:59:59']"
                      :disabled="!ad.isDateEditable"
                      size="small"
                    />
                    <div class="ad-actions">
                      <el-button
                        size="mini"
                        :icon="ad.isDateEditable ? 'el-icon-check' : 'el-icon-edit'"
                        :disabled="ad.savingDates"
                        @click="toggleUserAdEdit(ad)"
                      />
                      <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeUserAd(ad)" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="ads-empty-state">
            <i class="el-icon-picture-outline"></i>
            <p>No user ads assigned</p>
          </div>
        </div>

        <!-- Network Ads Section -->
        <div class="ads-section" style="margin-top: 24px;">
          <h3 class="section-title">
            <i class="el-icon-connection"></i> Network Ads
            <el-tag size="mini" type="success" style="margin-left: 8px;">{{ paidAdsList.length }}</el-tag>
          </h3>
          <div v-if="paidAdsList.length > 0" class="ads-list">
            <div
              v-for="(ad, idx) in paidAdsList"
              :key="'paid-' + (ad.id || idx)"
              class="ad-item paid-ad-item"
            >
              <div class="ad-image-wrap">
                <span v-if="ad.badge" class="ad-badge">{{ ad.badge }}</span>
                <video
                  v-if="ad.isVideo"
                  :src="ad.image"
                  class="ad-image ad-video"
                  muted loop playsinline preload="metadata"
                ></video>
                <img v-else :src="ad.image" :alt="ad.title" class="ad-image" @error="onAdImageError" />
              </div>
              <div class="ad-info">
                <h4 class="ad-title">{{ ad.title }}</h4>
                <p class="ad-brand">{{ ad.brand }}<span v-if="ad.pushedBy"> · Pushed by {{ ad.pushedBy }}</span></p>
              </div>
              <el-tag size="mini" :type="ad.displayLabel === 'Banner' ? 'warning' : 'info'">
                <i :class="ad.displayLabel === 'Banner' ? 'el-icon-picture' : 'el-icon-monitor'"></i>
                {{ ad.displayLabel || 'Screen' }}
              </el-tag>
            </div>
          </div>
          <div v-else class="ads-empty-state">
            <i class="el-icon-picture-outline"></i>
            <p>No Network Ads assigned</p>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/interface/index';
import url from '@/interface/url';
import methods from '@/interface/method';

export default {
  name: 'MachineAdvertGroup',
  props: {
    // 深链预筛选：来自设备详情飞出「广告」页签的 "Manage advertising" 跳转
    initialEquipmentNo: { type: String, default: '' }
  },
  data() {
    return {
      filters: {
        equipmentNo: '',
        equipmentName: '',
        groupName: ''
      },
      currentPage: 1,
      pageSize: 10,
      showAdsModal: false,
      selectedMachine: null,
      machines: [],
      loading: false,
      totalCount: 0,
      totalPages: 1,
      modalUserAds: [],
      modalPaidAds: []
    };
  },
  computed: {
    userAdsList() {
      return this.modalUserAds || [];
    },
    paidAdsList() {
      return this.modalPaidAds || [];
    },
    individualUserAds() {
      return this.userAdsList.filter(a => this.userAdSource(a) === 1);
    },
    groupUserAds() {
      return this.userAdsList.filter(a => this.userAdSource(a) === 0);
    },
    otherUserAds() {
      return this.userAdsList.filter(a => {
        const s = this.userAdSource(a);
        return s !== 0 && s !== 1;
      });
    }
  },
  mounted() {
    if (this.initialEquipmentNo) {
      this.filters.equipmentNo = this.initialEquipmentNo;
    }
    this.fetchPage();
  },
  methods: {
    parseBackendDateTime(str) {
      if (!str) return null;
      const s = String(str).trim();
      if (!s) return null;
      const normalized = s.replace(/-/g, '/');
      const d = new Date(normalized);
      return Number.isNaN(d.getTime()) ? null : d;
    },
    isExpiredAd(ad) {
      if (!ad) return false;
      const endRaw = ad.endTime != null ? ad.endTime : ad.end_time;
      const end = this.parseBackendDateTime(endRaw);
      if (!end) return false;
      return end.getTime() < Date.now();
    },
    fetchPage() {
      this.loading = true;
      const params = {
        pageIndex: this.currentPage,
        pageSize: this.pageSize,
        isSearchCount: true
      };
      if (this.filters.equipmentNo.trim()) params.equipmentNo = this.filters.equipmentNo.trim();
      if (this.filters.equipmentName.trim()) params.equipmentName = this.filters.equipmentName.trim();
      if (this.filters.groupName.trim()) params.groupName = this.filters.groupName.trim();

      return request.request(url.userEquipmentAd.page, methods.get, params)
        .then(res => {
          const ok = res.code === 0 || res.code === 200;
          if (ok && res.data) {
            const data = res.data;
            const records = data.records || data.list || (Array.isArray(data) ? data : []);
            this.machines = records.map(item => this.mapMachineRow(item));
            this.totalCount = data.total != null ? data.total : records.length;
            this.totalPages = data.pages != null ? data.pages : (this.totalCount > 0 ? Math.ceil(this.totalCount / this.pageSize) : 1);
            this.currentPage = data.current != null ? data.current : this.currentPage;
            this.pageSize = data.size != null ? data.size : this.pageSize;
          } else {
            this.$message.error(res.msg || 'Failed to load machines');
          }
          return res;
        })
        .catch(err => {
          console.error('fetchPage', err);
          this.$message.error('Failed to load machines');
          throw err;
        })
        .finally(() => { this.loading = false; });
    },
    mapMachineRow(item) {
      const adList = item.ad || [];
      const activeAds = adList.filter(a => !this.isExpiredAd(a));
      const userAdCount = activeAds.filter(a => a.adType === 0 || a.adType == null).length;
      const paidAdCount = activeAds.filter(a => a.adType === 1).length;
      const name = item.equipmentName || item.equipmentNo || '';
      const machineId = item.equipmentNo || '';
      const groupDisplay = item.groupName != null && item.groupName !== '' ? item.groupName : '–';
      return {
        id: item.equipmentNo || item.equipmentId || name,
        equipmentId: item.equipmentId || item.equipment_id || null,
        name,
        machineId,
        groupDisplay,
        userAdCount,
        paidAdCount,
        adCount: userAdCount,
        paidAds: paidAdCount,
        ad: activeAds
      };
    },
    viewAds(machine) {
      this.selectedMachine = machine;
      this.showAdsModal = true;
      const adList = (machine.ad || []).filter(a => !this.isExpiredAd(a));
      const userAds = adList.filter(a => a.adType === 0 || a.adType == null).map(a => this.mapAdToModal(a, false));
      const paidAds = adList.filter(a => a.adType === 1).map(a => this.mapAdToModal(a, true));
      this.modalUserAds = userAds;
      this.modalPaidAds = paidAds;
    },
    userAdSource(ad) {
      if (!ad || ad.source == null || ad.source === '') return null;
      const n = Number(ad.source);
      return Number.isNaN(n) ? null : n;
    },
    mapAdToModal(ad, isPaid) {
      const creativeAdId = ad.adId != null ? ad.adId : null;
      const assignmentItemId = !isPaid && ad.id != null && ad.id !== '' ? String(ad.id) : null;
      const rowKey = assignmentItemId || creativeAdId || `ad-${Math.random()}`;
      const title = ad.head || ad.title || '';
      const brand = ad.advertiser || ad.brand || '';
      const image = ad.url || ad.thum || ad.image || '';
      const displayLabel = ad.display === 2 ? 'Banner' : 'Screen';
      const isVideo = ad.type === 1;
      const sourceRaw = ad.source;
      const source = sourceRaw != null && sourceRaw !== '' ? Number(sourceRaw) : null;
      return {
        id: rowKey,
        assignmentItemId,
        adId: creativeAdId,
        title,
        brand,
        image,
        displayLabel,
        screen: 'Screen',
        pushedBy: isPaid ? (ad.pushedBy || ad.pushedByUser || '') : undefined,
        badge: isPaid ? (ad.badge || '') : undefined,
        isVideo,
        dateRange: ad.startTime && ad.endTime ? [ad.startTime, ad.endTime] : null,
        isDateEditable: false,
        source: Number.isNaN(source) ? null : source,
        adGroupName: !isPaid ? (ad.adGroupName || ad.ad_group_name || '') : undefined,
        savingDates: false
      };
    },
    toggleUserAdEdit(ad) {
      if (!ad) return;
      if (ad.isDateEditable) {
        this.saveUserAdDates(ad);
      } else {
        ad.isDateEditable = true;
      }
    },
    saveUserAdDates(ad) {
      if (!ad || !ad.assignmentItemId) {
        this.$message.warning('Missing assignment item id; refresh the list and try again.');
        return;
      }
      if (!ad.dateRange || ad.dateRange.length !== 2) {
        this.$message.warning('Select start and end date/time.');
        return;
      }
      if (ad.savingDates) return;
      ad.savingDates = true;
      request.request(url.userEquipmentAd.editAdDate, methods.post, {
        id: ad.assignmentItemId,
        startTime: ad.dateRange[0],
        endTime: ad.dateRange[1]
      })
        .then(res => {
          const ok = res.code === 0 || res.code === 200;
          if (ok) {
            ad.isDateEditable = false;
            this.$message.success(res.msg || 'Schedule updated');
            this.refreshModalAdsAfterChange();
          } else {
            this.$message.error(res.msg || 'Update failed');
          }
        })
        .catch(() => {
          this.$message.error('Update failed');
        })
        .finally(() => {
          ad.savingDates = false;
        });
    },
    removeUserAd(ad) {
      if (!ad || !ad.assignmentItemId) {
        this.$message.warning('Cannot remove this assignment; refresh the list and try again.');
        return;
      }
      this.$confirm('Remove this ad from the machine?', 'Confirm', {
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(() => {
          return request.request(url.userEquipmentAd.deleteEquipmentAd, methods.post, { id: ad.assignmentItemId });
        })
        .then(res => {
          if (!res) return;
          const ok = res.code === 0 || res.code === 200;
          if (ok) {
            const idx = this.modalUserAds.indexOf(ad);
            if (idx >= 0) this.modalUserAds.splice(idx, 1);
            this.$message.success(res.msg || 'Removed');
            this.refreshModalAdsAfterChange();
          } else {
            this.$message.error(res.msg || 'Remove failed');
          }
        })
        .catch(err => {
          if (err !== 'cancel' && err !== 'close') {
            this.$message.error('Remove failed');
          }
        });
    },
    refreshModalAdsAfterChange() {
      if (!this.showAdsModal || !this.selectedMachine) return;
      const eqId = this.selectedMachine.equipmentId;
      const machineId = this.selectedMachine.machineId;
      this.fetchPage().then(() => {
        if (!this.showAdsModal) return;
        const m = this.machines.find(row =>
          (eqId && row.equipmentId === eqId) ||
          (!eqId && machineId && row.machineId === machineId)
        );
        if (m) this.viewAds(m);
      });
    },
    onAdImageError(e) {
      if (e && e.target) {
        e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="#f0f0f0" width="80" height="80"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999" font-size="10" font-family="sans-serif">No image</text></svg>');
      }
    },
    closeModal() {
      this.showAdsModal = false;
      this.selectedMachine = null;
      this.modalUserAds = [];
      this.modalPaidAds = [];
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchPage();
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.fetchPage();
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchPage();
    },
    handleReset() {
      this.filters.equipmentNo = '';
      this.filters.equipmentName = '';
      this.filters.groupName = '';
      this.currentPage = 1;
      this.fetchPage();
    }
  }
};
</script>

<style scoped>
.paid-ads-count {
  color: var(--vtm-mint-ink);
  font-weight: 500;
}

/* Ad item layout inside dialog */
.ads-section {
  margin-bottom: 8px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
}

.user-ads-subsection {
  margin-bottom: 16px;
}

.subsection-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--vtm-ink-2);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 8px 0;
}

.ads-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ad-item {
  border: 1px solid var(--vtm-line);
  border-radius: var(--radius-sm);
  padding: 12px;
  background: var(--vtm-surface);
}

.ad-top-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ad-image-wrap {
  position: relative;
  flex-shrink: 0;
}

.ad-image {
  width: 72px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
  background: var(--vtm-well);
}

.ad-video {
  width: 72px;
  height: 56px;
}

.ad-info {
  flex: 1;
  min-width: 0;
}

.ad-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ad-brand {
  font-size: 12px;
  color: var(--vtm-ink-2);
  margin: 0;
}

.ad-group-name {
  font-size: 11px;
  color: var(--vtm-ink-3);
  margin: 2px 0 0 0;
}

.ad-bottom-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.ad-bottom-row-actions-only {
  justify-content: flex-end;
}

.ad-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.ad-actions-end {
  margin-left: auto;
}

.ad-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  background: var(--vtm-mint);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
}

.ads-empty-state {
  padding: 24px;
  text-align: center;
  background: var(--vtm-well);
  border: 1px dashed var(--vtm-line);
  border-radius: var(--radius-sm);
  color: var(--vtm-ink-3);
}

.ads-empty-state i {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

.ads-empty-state p {
  margin: 0;
  font-size: 13px;
}
</style>
